import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useStore } from "@/contexts/StoreContext";
import { useWishlist } from "@/contexts/WishlistContext";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { BrandLogo } from "@/components/BrandLogo";
import AnnouncementBar from "@/components/customer/AnnouncementBar";
import PincodeDialog from "@/components/customer/PincodeDialog";
import {
  MapPin, Search, ShoppingCart, User, LogOut, ClipboardList, Bell,
  UserCircle2, ShieldCheck, Heart, Mic, Clock, TrendingUp, X, Package,
} from "lucide-react";

const RECENT_KEY = "bb_recent_searches_v1";
const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; } };
const writeRecent = (arr) => { try { localStorage.setItem(RECENT_KEY, JSON.stringify(arr.slice(0, 8))); } catch { /* noop */ } };

export default function CustomerHeader() {
  const nav = useNavigate();
  const { user, logout } = useAuth();
  const { itemCount, subtotal } = useCart() || { itemCount: 0, subtotal: 0 };
  const { count: wishCount } = useWishlist();
  const { pincode, setPincode, isServiceable, ui } = useStore();
  const hdr = ui?.header || {};
  const searchPh = hdr.search_placeholder || 'Search "atta", "milk", "soap"…';
  const deliveryPrefix = hdr.delivery_text || "Delivery in";
  const showTimer = hdr.show_delivery_timer !== false;
  const [q, setQ] = useState("");
  const [store, setStore] = useState(null);
  const [pinOpen, setPinOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [recent, setRecent] = useState(readRecent());
  const [popular, setPopular] = useState([]);
  const searchWrapRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (user) api.get("/store/settings").then((r) => setStore(r.data)).catch(() => {});
  }, [user]);

  // Popular = trending products (first-8)
  useEffect(() => {
    if (!user) return;
    api.get("/products?limit=8").then((r) => setPopular((r.data.items || []).filter((p) => !/^TEST[_-]/i.test(p.name || "")).slice(0, 8))).catch(() => {});
  }, [user]);

  // Sprint — Blinkit-style smart search:
  // Show up to 4 instant suggestions with a lightweight 120ms debounce.
  // Kick in from 1 character so results appear as the customer starts typing.
  useEffect(() => {
    const term = q.trim().replace(/\s+/g, " ");
    if (!user || term.length < 1) { setSuggestions([]); setSearching(false); return; }
    setSearching(true);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/products?q=${encodeURIComponent(term)}&limit=4`);
        setSuggestions((data.items || []).filter((p) => !/^TEST[_-]/i.test(p.name || "")).slice(0, 4));
      } catch { setSuggestions([]); }
      finally { setSearching(false); }
    }, 120);
    return () => clearTimeout(debounceRef.current);
  }, [q, user]);

  // Close dropdown on outside click
  useEffect(() => {
    const onClick = (e) => { if (searchWrapRef.current && !searchWrapRef.current.contains(e.target)) setFocused(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submit = (e, term) => {
    if (e) e.preventDefault();
    const t = (term ?? q).trim();
    if (!t) return;
    // Save to recent (unique, prepend)
    setRecent((prev) => {
      const next = [t, ...prev.filter((x) => x !== t)].slice(0, 8);
      writeRecent(next);
      return next;
    });
    setFocused(false);
    // Sprint — Blinkit-style route (/search?q=…) with legacy fallback to /shop.
    nav(`/search?q=${encodeURIComponent(t)}`);
  };

  // Sprint — Highlight matching tokens inside a product name for the dropdown.
  const highlight = (text, term) => {
    const value = String(text || "");
    const t = (term || "").trim();
    if (!t) return value;
    const tokens = t.split(/\s+/).filter(Boolean).map((tok) => tok.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
    if (!tokens.length) return value;
    const re = new RegExp(`(${tokens.join("|")})`, "gi");
    const parts = value.split(re);
    return parts.map((seg, i) =>
      re.test(seg)
        ? <mark key={i} className="bg-primary/15 text-primary font-semibold rounded-sm px-0.5">{seg}</mark>
        : <span key={i}>{seg}</span>,
    );
  };

  const clearRecent = () => { writeRecent([]); setRecent([]); };
  const dropRecent = (t) => setRecent((prev) => { const next = prev.filter((x) => x !== t); writeRecent(next); return next; });

  // Voice search — uses browser Web Speech API (Chrome/Edge). No backend.
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Voice search isn't supported in this browser. Please try Chrome or Edge."); return; }
    if (listening) { try { recogRef.current?.stop(); } catch { /* noop */ } return; }
    const r = new SR();
    r.lang = navigator.language || "en-IN";
    r.interimResults = false;
    r.maxAlternatives = 1;
    r.continuous = false;
    r.onstart = () => setListening(true);
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    r.onresult = (e) => {
      const term = (e.results?.[0]?.[0]?.transcript || "").trim();
      if (term) { setQ(term); submit(null, term); }
    };
    recogRef.current = r;
    try { r.start(); } catch { setListening(false); }
  };

  const deliveryLabel = pincode || store?.address?.city || "Set location";
  const isStaff = user && user.role !== "CUSTOMER";

  const showDropdown = focused && (q.trim().length > 0 || recent.length > 0 || popular.length > 0);

  return (
    <>
      <AnnouncementBar />
      <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur-md" data-testid="customer-header">
        {/* Sprint 61 — Mobile: delivery pill on left, ACCOUNT on right (moved from
            top header row). Delivery is still tap-to-set-pincode; account opens
            profile page (Sign in when signed-out). */}
        <div className="md:hidden w-full flex items-stretch border-b border-border/50 bg-secondary/30">
          <button
            className="flex-1 min-w-0 flex items-center gap-2 px-3 py-1.5 text-left"
            onClick={() => setPinOpen(true)}
            data-testid="mobile-delivery-bar"
          >
            <MapPin className={`w-3.5 h-3.5 shrink-0 ${isServiceable ? "text-primary" : "text-destructive"}`} />
            <div className="min-w-0 flex-1 leading-tight">
              {showTimer && (
                <div className="text-[10px] font-semibold text-foreground/90">
                  {deliveryPrefix} <span className="text-primary">{store?.commerce?.zones?.[0]?.eta_minutes || 15} min</span>
                </div>
              )}
              <div className="text-[10.5px] text-muted-foreground truncate">
                Home · <span className="font-semibold text-foreground/80">{deliveryLabel}</span> ▾
              </div>
            </div>
          </button>
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="shrink-0 px-3 flex items-center justify-center border-l border-border/50 hover:bg-white/60 active:bg-white/80 transition"
                  data-testid="mobile-account-btn"
                  aria-label="Account"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-accent text-white grid place-items-center text-[11px] font-bold shadow-sm ring-2 ring-white">
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={8}
                className="w-60 rounded-xl shadow-xl border border-border/50 p-1.5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[side=bottom]:slide-in-from-top-2"
                data-testid="mobile-account-dropdown"
              >
                <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 pt-1">Signed in as {user.role}</DropdownMenuLabel>
                <div className="px-2 pb-1.5">
                  <div className="text-sm font-semibold truncate">{user.name || user.email}</div>
                  {user.phone && <div className="text-[11px] text-muted-foreground truncate">{user.phone}</div>}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild className="rounded-lg px-2.5 py-2 cursor-pointer transition hover:bg-primary/5 focus:bg-primary/5"><Link to="/account" data-testid="m-menu-profile"><UserCircle2 className="w-4 h-4 mr-2 text-primary" />My Profile</Link></DropdownMenuItem>
                <DropdownMenuItem asChild className="rounded-lg px-2.5 py-2 cursor-pointer transition hover:bg-primary/5 focus:bg-primary/5"><Link to="/account/orders" data-testid="m-menu-orders"><ClipboardList className="w-4 h-4 mr-2 text-primary" />My Orders</Link></DropdownMenuItem>
                <DropdownMenuItem asChild className="rounded-lg px-2.5 py-2 cursor-pointer transition hover:bg-primary/5 focus:bg-primary/5"><Link to="/wishlist" data-testid="m-menu-wishlist"><Heart className="w-4 h-4 mr-2 text-primary" />My Wishlist</Link></DropdownMenuItem>
                {isStaff && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild className="rounded-lg px-2.5 py-2"><Link to="/admin" data-testid="m-menu-admin"><ShieldCheck className="w-4 h-4 mr-2" />Admin console</Link></DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="rounded-lg px-2.5 py-2 cursor-pointer text-destructive hover:bg-destructive/5 focus:bg-destructive/5 focus:text-destructive" data-testid="m-menu-logout"><LogOut className="w-4 h-4 mr-2" />Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              type="button"
              onClick={() => nav("/login")}
              className="shrink-0 px-3 flex items-center justify-center border-l border-border/50 hover:bg-white/60 active:bg-white/80 transition"
              data-testid="mobile-account-btn"
              aria-label="Sign in"
            >
              <div className="w-7 h-7 rounded-full bg-primary text-white grid place-items-center shadow-sm ring-2 ring-white">
                <UserCircle2 className="w-4 h-4" />
              </div>
            </button>
          )}
        </div>
        <div className="max-w-7xl mx-auto px-3 md:px-6 h-14 md:h-16 flex items-center gap-2 md:gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-link" aria-label="Babu Baazar home">
            <BrandLogo mark size={30} />
          </Link>

          <button
            className="hidden md:flex items-center gap-2 text-sm min-w-0 max-w-[220px] shrink-0 rounded-lg px-2.5 py-1.5 hover:bg-secondary/60 transition"
            onClick={() => setPinOpen(true)}
            data-testid="delivery-location"
          >
            <MapPin className={`w-4 h-4 shrink-0 ${isServiceable ? "text-primary" : "text-destructive"}`} />
            <div className="min-w-0 text-left leading-tight">
              {showTimer && <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{deliveryPrefix} {store?.commerce?.zones?.[0]?.eta_minutes || 15} min</div>}
              <div className="text-[13px] font-semibold truncate flex items-center gap-0.5">{deliveryLabel}<span className="text-muted-foreground">▾</span></div>
            </div>
          </button>

          {/* Sticky search — hero of the header, Blinkit style. Larger with subtle grey border. */}
          <div ref={searchWrapRef} className="flex-1 relative">
            <form onSubmit={submit} data-testid="header-search-form">
              <Search className="w-4 h-4 md:w-5 md:h-5 absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => setFocused(true)}
                placeholder={searchPh}
                className="pl-11 pr-11 h-11 md:h-12 rounded-xl bg-white border border-gray-300 hover:border-gray-400 focus-visible:border-primary focus-visible:ring-0 focus-visible:bg-white text-[13px] md:text-sm placeholder:text-gray-500 shadow-none"
                data-testid="header-search"
              />
              <button
                type="button"
                onClick={startVoice}
                className={`absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 grid place-items-center rounded-full transition ${listening ? "text-white bg-primary animate-pulse" : "text-muted-foreground hover:text-primary hover:bg-primary/10"}`}
                aria-label={listening ? "Listening…" : "Voice search"}
                aria-pressed={listening}
                data-testid="voice-search"
              >
                <Mic className="w-4 h-4" />
              </button>
            </form>

            {showDropdown && (
              <div className="absolute z-50 top-full left-0 right-0 mt-2 rounded-xl border bg-card shadow-lg overflow-hidden fade-in-up" data-testid="search-dropdown">
                {/* Live suggestions */}
                {q.trim().length >= 2 && (
                  <div className="p-2 max-h-[70vh] overflow-y-auto" data-testid="search-suggestions">
                    <div className="flex items-center justify-between px-2 py-1">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                        {searching ? "Searching…" : suggestions.length ? `Results for “${q.trim()}”` : "No matches"}
                      </div>
                      {suggestions.length > 0 && (
                        <button onClick={(e) => submit(e)} className="text-[11px] font-semibold text-primary hover:underline" data-testid="see-all-results">See all →</button>
                      )}
                    </div>
                    {suggestions.length === 0 && !searching ? (
                      <div className="px-2 py-4 space-y-2" data-testid="search-empty">
                        <div className="text-sm font-semibold">No products found</div>
                        <div className="text-[12px] text-muted-foreground">Try shorter or different keywords, or browse below.</div>
                        {popular.length > 0 && (
                          <div className="pt-2">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Trending right now</div>
                            <div className="grid grid-cols-2 gap-1.5">
                              {popular.slice(0, 6).map((p) => (
                                <button
                                  key={p.id}
                                  onClick={() => { setFocused(false); nav(`/product/${p.id}`); }}
                                  className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-secondary/60 text-left"
                                >
                                  <div className="w-8 h-8 rounded bg-secondary overflow-hidden grid place-items-center shrink-0">
                                    {p.thumbnail_url ? <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <Package className="w-3 h-3 text-muted-foreground" />}
                                  </div>
                                  <span className="text-sm truncate">{p.name}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-0.5">
                        {suggestions.map((p) => {
                          const mrp = Number(p.mrp || 0);
                          const sp  = Number(p.selling_price || 0);
                          const off = mrp > sp ? Math.round(((mrp - sp) / mrp) * 100) : 0;
                          return (
                            <button
                              key={p.id}
                              onClick={() => { setFocused(false); nav(`/product/${p.id}`); }}
                              className="w-full flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-secondary/60 text-left"
                              data-testid={`sugg-${p.id}`}
                            >
                              <div className="relative w-11 h-11 rounded bg-secondary overflow-hidden grid place-items-center shrink-0">
                                {p.thumbnail_url ? <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <Package className="w-4 h-4 text-muted-foreground" />}
                                {off > 0 && (
                                  <span className="absolute top-0 left-0 bg-primary text-primary-foreground text-[8px] font-black leading-none px-1 py-[1px] rounded-br">{off}%</span>
                                )}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-medium truncate">{highlight(p.name, q)}</div>
                                <div className="text-[11px] text-muted-foreground truncate">
                                  {p.brand_name && <span className="text-foreground/70">{p.brand_name}</span>}
                                  {p.brand_name && (p.weight || p.category_name) && <span> · </span>}
                                  {p.weight ? `${p.weight}${p.weight_unit || "g"}` : (p.category_name || "")}
                                </div>
                              </div>
                              <div className="text-right shrink-0 leading-tight">
                                <div className="text-sm font-bold tabular-nums">{money(sp)}</div>
                                {off > 0 && <div className="text-[10px] text-muted-foreground line-through tabular-nums">{money(mrp)}</div>}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {/* Recent */}
                {!q.trim() && recent.length > 0 && (
                  <div className="p-2 border-t first:border-t-0" data-testid="search-recent">
                    <div className="flex items-center justify-between px-2 py-1">
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold flex items-center gap-1"><Clock className="w-3 h-3" />Recent</div>
                      <button onClick={clearRecent} className="text-[10px] text-primary hover:underline" data-testid="clear-recent">Clear</button>
                    </div>
                    <div className="flex flex-wrap gap-1.5 px-2 pb-1">
                      {recent.map((t) => (
                        <div key={t} className="inline-flex items-center gap-1 rounded-full bg-secondary text-sm px-3 py-1 group hover:bg-secondary/80">
                          <button onClick={() => submit(null, t)} className="truncate max-w-[180px]" data-testid={`recent-${t}`}>{t}</button>
                          <button onClick={() => dropRecent(t)} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Popular */}
                {!q.trim() && popular.length > 0 && (
                  <div className="p-2 border-t" data-testid="search-popular">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-2 py-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" />Popular right now</div>
                    <div className="grid grid-cols-2 gap-1">
                      {popular.slice(0, 6).map((p) => (
                        <button
                          key={p.id}
                          onClick={() => { setFocused(false); nav(`/product/${p.id}`); }}
                          className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-secondary/60 text-left"
                          data-testid={`popular-${p.id}`}
                        >
                          <div className="w-8 h-8 rounded bg-secondary overflow-hidden grid place-items-center shrink-0">
                            {p.thumbnail_url ? <img src={p.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <Package className="w-3 h-3 text-muted-foreground" />}
                          </div>
                          <span className="text-sm truncate">{p.name}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex items-center gap-0.5 md:gap-1 shrink-0">
            {/* Notifications — desktop only */}
            <Button variant="ghost" size="icon" className="relative hidden md:inline-flex h-9 w-9" onClick={() => alert("No new notifications.")} data-testid="header-bell-btn" aria-label="Notifications">
              <Bell className="w-5 h-5" />
            </Button>

            {/* Cart — Sprint 64b: ALWAYS visible. Mobile: RED bg + WHITE icon.
                Badge: RED with WHITE count, ringed with white for separation,
                perfectly centered via `grid place-items-center`. */}
            <Button
              asChild
              variant="ghost"
              className="relative gap-2 h-10 rounded-xl bg-primary text-primary-foreground shadow-[0_2px_10px_-2px_rgba(0,0,0,0.18)] hover:bg-primary hover:brightness-105 md:bg-primary/10 md:text-foreground md:shadow-none md:hover:bg-primary/15 md:hover:brightness-100 md:px-3"
              data-testid="header-cart-btn"
            >
              <Link to="/cart">
                <ShoppingCart className="w-5 h-5 md:text-primary" />
                <div className="hidden md:flex flex-col items-start leading-none">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{itemCount} item{itemCount !== 1 ? "s" : ""}</span>
                  <span className="text-sm font-semibold" data-testid="header-cart-total">{money(subtotal)}</span>
                </div>
                {itemCount > 0 && (
                  <span
                    key={itemCount}
                    className="absolute -top-1.5 -right-1.5 w-[18px] h-[18px] rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none grid place-items-center ring-2 ring-white shadow-sm md:hidden animate-in zoom-in-50 duration-200"
                    data-testid="header-cart-badge"
                  >
                    <span className="tabular-nums translate-y-[0.5px]">{itemCount}</span>
                  </span>
                )}
              </Link>
            </Button>

            {/* Sprint 67 — Desktop premium account dropdown (matches mobile).
                Avatar-only trigger, no first-name text. Blinkit-style menu. */}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="hidden md:flex h-9 w-9 p-0 rounded-full" data-testid="account-menu" aria-label="Account">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-accent to-primary text-white grid place-items-center text-[12px] font-bold shadow-sm ring-2 ring-white">
                      {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  sideOffset={8}
                  className="w-64 rounded-xl shadow-xl border border-border/50 p-1.5 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[side=bottom]:slide-in-from-top-2"
                  data-testid="account-dropdown"
                >
                  <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 pt-1">Signed in as {user.role}</DropdownMenuLabel>
                  <div className="px-2 pb-1.5">
                    <div className="text-sm font-semibold truncate">{user.name || user.email}</div>
                    {user.phone && <div className="text-[11px] text-muted-foreground truncate">{user.phone}</div>}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild className="rounded-lg px-2.5 py-2 cursor-pointer transition hover:bg-primary/5 focus:bg-primary/5"><Link to="/account" data-testid="menu-profile"><UserCircle2 className="w-4 h-4 mr-2 text-primary" />My Profile</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild className="rounded-lg px-2.5 py-2 cursor-pointer transition hover:bg-primary/5 focus:bg-primary/5"><Link to="/account/orders" data-testid="menu-orders"><ClipboardList className="w-4 h-4 mr-2 text-primary" />My Orders</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild className="rounded-lg px-2.5 py-2 cursor-pointer transition hover:bg-primary/5 focus:bg-primary/5"><Link to="/wishlist" data-testid="menu-wishlist"><Heart className="w-4 h-4 mr-2 text-primary" />My Wishlist</Link></DropdownMenuItem>
                  {isStaff && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild className="rounded-lg px-2.5 py-2"><Link to="/admin" data-testid="menu-admin"><ShieldCheck className="w-4 h-4 mr-2" />Admin console</Link></DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} className="rounded-lg px-2.5 py-2 cursor-pointer text-destructive hover:bg-destructive/5 focus:bg-destructive/5 focus:text-destructive" data-testid="menu-logout"><LogOut className="w-4 h-4 mr-2" />Sign Out</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button asChild className="btn-primary-glow" size="sm" data-testid="header-signin">
                <Link to="/login"><User className="w-4 h-4 mr-1" />Sign in</Link>
              </Button>
            )}
          </div>
        </div>

        {/* Sub-nav removed — top nav items live in MobileBottomNav (now shown on desktop too). */}
      </header>

      <PincodeDialog open={pinOpen} onOpenChange={setPinOpen} />
    </>
  );
}
